/**
 * @file   src\components\MemberWantAd\WantAdLocation.js
 * @brief  This file is responsible for want ad location section in want ad creation for member user.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import InputGroupControl from "../Common/InputGroup";
import { handleTextBoxChange, capitalizeString } from "../../actions/common";
import GitchaNotAvailableModal from "../Common/GitchaNotAvailableModal";
import {
  clearLocationStatesExceptLatLng,
  createS3Folder,
  filterArrayBasedOnKey,
  getLocalStorage,
  getLocalityFromZipCode,
} from "../../helpers/common";

import {
  DEFAULT_WANT_AD_MAP_ZOOM,
  MAP_EVENT_CLICK,
  MAP_EVENT_LOCATION_CHANGE,
  MAP_EVENT_MARKER_DRAG,
  SHORT_RENT,
  BUY,
  LONG_RENT,
  WANT_AD_KEY,
} from "../../constants/common";
import GeoLocation from "../Common/GeoLocation";

import {
  getGeoLocation,
  checkGitchaAvailable,
} from "../../services/commonServices";

import { convertToNumber } from "../../helpers/validations";
import WantAdGoogleMap from "./WantAdGoogleMap";
import { usePrevious } from "../../hooks/usePrevious";
import SelectDropDown from "../Common/SelectDropDown";
import TextBox from "../Common/TextBox";
import { getRadiusList } from "../../services/wantAdService";
import ZipCloseIcon from "../../images/zipcode-close-icon.svg";
import FireDisplacedCheckbox from "../Common/v2/FireDisplacedCheckbox";
import { useMemo } from "react";
const WantAdLocation = (props) => {
  const {
    handleContinueButtonState,
    handleFormValues,
    location,
    isEdit = false,
    lat,
    lng,
    mapZoom,
    mapType,
    targetRef,
    mapRadius,
    neighborHood,
    highSchool,
    fireDisplaced,
    state,
    adType,
    county,
    isGitchaAvailable,
    zipCode,
    zipIdValues,
  } = props;
  const defaultProps = useMemo(
    () => ({
      center: {
        lat: convertToNumber(lat),
        lng: convertToNumber(lng),
      },
      zoom: mapZoom || DEFAULT_WANT_AD_MAP_ZOOM,
    }),
    [lat, lng, mapZoom],
  );
  const [showMarker, setShowMarker] = useState(true);
  const [isClickOrDrag, setClickDrag] = useState(false);
  const [eventType, setEventType] = useState(null);
  const HandleMapClickOrDrag = (status) => {
    setClickDrag(status);
  };
  const handleShowMarker = (markerStatus) => {
    setShowMarker(markerStatus);
  };

  const [radiusList, setRadiusList] = useState([]);
  const [locationError, setLocationError] = useState("");
  const [locationList, setLocationList] = useState([]);
  const [gitchaModalShow, setGitchaModal] = useState(false);
  const [isCalifornia, setIsCalifornia] = useState(
    state === "CA" ? true : false,
  );

  const handleGitchaAvailableModal = (state) => {
    if (state === false) {
      clearLocationStates();
    }
    handleShowMarker(true);
    setGitchaModal(state);
  };

  const [zipList, setZipList] = useState([]);
  const [zipCodeArray, setZipCodeArray] = useState(
    zipCode?.length > 0 ? zipCode : [],
  );
  const [zipCodeEnterdValue, setZipCodeEnterdValue] = useState(null);
  const [showPolygon, setShowPolygon] = useState(
    zipCode?.length > 0 ? true : false,
  );
  const wantAdDetails = getLocalStorage(WANT_AD_KEY);

  useEffect(() => {
    if (zipCode && zipCode?.length > 0) {
      setZipCodeArray(zipCode);
    } else {
      setZipCodeArray([]);
    }
  }, [zipCode]);
  /**
   * To get address details of searched location from google api
   * @param {*} param0
   */
  const getAddresseDetails = async ({ eventType, reqPayload }) => {
    setEventType(eventType);
    await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");
      let locArray = [];
      if (results?.length > 0) {
        const formatted_address = _.get(results?.[0], "formatted_address", "");
        if (
          eventType === MAP_EVENT_LOCATION_CHANGE &&
          results?.length > 0 &&
          formatted_address !== "United States"
        ) {
          locArray.push(results[0]);
          setLocationList(locArray);
        }

        const firstLocationResult = _.get(results[0], "geometry.location", "");
        const firstAddressComponent = _.get(
          results[0],
          "address_components",
          "",
        );

        const lat = _.get(firstLocationResult, "lat", "");
        const lng = _.get(firstLocationResult, "lng", "");
        let addressObject = { lat: lat, lng: lng };
        if (
          eventType === MAP_EVENT_CLICK ||
          eventType === MAP_EVENT_MARKER_DRAG
        ) {
          try {
            if (firstAddressComponent && firstAddressComponent.length > 0) {
              const street_no = firstAddressComponent.filter(function (ca) {
                return ca.types?.[0] === "street_number";
              });

              if (street_no.length) {
                addressObject.street_no = _.get(
                  street_no?.[0],
                  "short_name",
                  "",
                );
              }

              const route = firstAddressComponent.filter(function (ca) {
                return ca.types?.[0] === "route";
              });

              if (route.length) {
                addressObject.route = _.get(route?.[0], "short_name", "");
                addressObject.street = _.get(route?.[0], "short_name", "");
              }
              const locality = firstAddressComponent.filter(function (ca) {
                if (isCalifornia) {
                  return (
                    ca.types?.[0] === "locality" ||
                    ca.types?.[0] === "administrative_area_level_2"
                  );
                } else {
                  return ca.types?.[0] === "locality";
                }
              });
              if (locality.length) {
                addressObject.city = _.get(locality?.[0], "long_name", "");
              }
              const county = firstAddressComponent.filter(function (ca) {
                return ca.types?.[0] === "administrative_area_level_2";
              });

              if (county.length) {
                addressObject.county = _.get(county?.[0], "short_name", "");
              }
              const state = firstAddressComponent.filter(function (ca) {
                return ca.types?.[0] === "administrative_area_level_1";
              });
              if (state.length) {
                addressObject.state = _.get(state?.[0], "short_name", "");
              }
              const zip = firstAddressComponent.filter(function (ca) {
                return ca.types?.[0] === "postal_code";
              });
              if (zip.length) {
                addressObject.zip = _.get(zip?.[0], "short_name", "");
              }
            }
          } catch {}
          isGitchaAvailableLocation(addressObject);
          const city = _.get(addressObject, "city", null);
          const state = _.get(addressObject, "state", null);
          if (city && state) {
            addressObject.location = `${city}, ${state}`;
          }
          handleFormValues(addressObject);
        } else {
          handleFormValues(addressObject);
        }
      } else {
        const isDisable = true;
        handleContinueButtonState(isDisable);
      }
    });
  };
  /**
   * To get radius list
   */
  const getRadiusLists = async () => {
    await getRadiusList().then((data) => {
      const results = _.get(data, "result");
      if (results?.length > 0) {
        setRadiusList(results);
      } else {
      }
    });
  };
  /**
   * To check ig gitcha is available
   * @param {*} param0
   * @returns
   */
  const checkGitchAvailability = async ({ zip, state, city, county }) => {
    const reqPayLoad = { county: county, state: state };
    return await checkGitchaAvailable(reqPayLoad).then((data) => {
      return data;
    });
  };

  useEffect(() => {
    if (
      eventType === MAP_EVENT_LOCATION_CHANGE &&
      isGitchaAvailable &&
      prevMapRadius === mapRadius &&
      !mapRadius &&
      zipCode?.length == 0 &&
      !zipCodeEnterdValue
    ) {
      handleFormValues({ mapRadius: 5, mapZoom: 10 });
    }
  }, [
    location,
    mapRadius,
    isGitchaAvailable,
    eventType,
    wantAdDetails?.wantAdId,
  ]);

  useEffect(() => {
    if (
      ((location && location !== "" && mapRadius && county) ||
        zipCode?.length > 0) &&
      (!wantAdDetails?.wantAdId ? isGitchaAvailable : true) // if its edit isGitchaAvailable true deafult
    ) {
      handleContinueButtonState();
    } else {
      const isDisable = true;
      handleContinueButtonState(isDisable);
    }
  }, [
    location,
    mapRadius,
    isGitchaAvailable,
    eventType,
    zipCode,
    wantAdDetails?.wantAdId,
  ]);

  useEffect(() => {
    getRadiusLists();
    if (!location) {
      const wantAdDetails = getLocalStorage(WANT_AD_KEY);
      const s3Folder = _.get(wantAdDetails, "s3ImageFolder", null);
      if (!s3Folder) {
        getFolderName();
      }
    }
  }, []);
  /**
   * To get folder name where image should be uploaded
   */
  const getFolderName = async () => {
    const isWantAd = true;
    await createS3Folder(isWantAd).then(function (result) {
      const folderName = result;
      handleFormValues({ s3ImageFolder: folderName });
    });
  };

  const prevMapRadius = usePrevious(mapRadius);
  /**
   * To handle location field changes
   * @param {*} e
   */
  const handleLocationChange = (e) => {
    handleFormValues({
      city: "",
      state: "",
      zip: "",
      county: "",
      isGitchaAvailable: false,
      zipCode: [],
      zipIdValues: [],
      zipId: [],
      forGitchaAvailablePopup: {},
    });
    setZipCodeArray([]);
    setZipCodeEnterdValue(null);
    const value = capitalizeString(handleTextBoxChange(e));
    const reqPayload = `address=${value}&components=country:US`;
    if (value.trim() !== "" && value.length > 3) {
      getAddresseDetails({
        reqPayload: reqPayload,
        eventType: MAP_EVENT_LOCATION_CHANGE,
      });
    } else {
      clearAddressList();
    }
    const isDisableContinueButton = value && value.trim() !== "" ? false : true;
    if (value && value.trim() !== "") {
      handleFormValues({ location: value });
    } else {
      clearLocationStates();
    }
  };
  /**
   * To clear already selected location
   */
  const clearLocationStates = () => {
    handleFormValues({
      location: "",
      city: "",
      state: "",
      zip: "",
      mapRadius: "",
      lat: "",
      lng: "",
      isGitchaAvailable: null,
      forGitchaAvailablePopup: {},
    });
  };
  /**
   * To check if gitcha is available in searched location
   * @param {*} addressObject
   */
  const isGitchaAvailableLocation = async (addressObject) => {
    const state = _.get(addressObject, "state", "");
    const city = _.get(addressObject, "city", "");
    let county = _.get(addressObject, "county", "");

    if (!county) {
      const reqPayload = `address=${city + ", " + state}`;
      await getGeoLocation(reqPayload).then((data) => {
        const results = _.get(data, "results");

        if (results?.length > 0) {
          const firstAddressComponent = _.get(
            results[0],
            "address_components",
            "",
          );

          try {
            if (firstAddressComponent && firstAddressComponent.length > 0) {
              const countyArray = firstAddressComponent.filter(function (ca) {
                return ca.types?.[0] === "administrative_area_level_2";
              });

              if (countyArray.length) {
                county = _.get(countyArray?.[0], "short_name", "");
              }
            }
          } catch {}
          handleFormValues({ county: county });
        }
      });
    }
    if (!county) {
      handleFormValues({ isGitchaAvailable: false });
      handleGitchaAvailableModal(true);
      clearZipCodeList();
      setZipCodeEnterdValue("");
    } else {
      checkGitchAvailability({
        county: county,
        state: state,
      }).then((response) => {
        const isGitchaAvailable = _.get(response, "result", "");
        handleFormValues({ isGitchaAvailable: isGitchaAvailable });
        if (!isGitchaAvailable) {
          handleGitchaAvailableModal(true);
          clearZipCodeList();
          setZipCodeEnterdValue("");
        }
      });
    }
  };
  /**
   * To handle selection from location suggestions
   * @param {*} address
   */
  const handleItemSelection = async (address) => {
    let isCalifornia = false;
    address?.address_components?.map((address) => {
      if (address.long_name === "California") {
        isCalifornia = true;
      }
    });
    setIsCalifornia(isCalifornia);
    clearLocationStatesExceptLatLng(handleFormValues);
    handleShowMarker(true);
    HandleMapClickOrDrag(false);
    const formatted_address = _.get(address, "formatted_address", "");
    const firstAddressComponent = _.get(address, "address_components", "");
    const latitude = _.get(address, "geometry.location.lat", ""); // removeFixedDecimalPart({value: _.get(address, "geometry.location.lat", ""), decimalPoint : DECIMAL_POINTS_IN_COORDICATES});
    const longitude = _.get(address, "geometry.location.lng", ""); // removeFixedDecimalPart({value: _.get(address, "geometry.location.lng", ""), decimalPoint : DECIMAL_POINTS_IN_COORDICATES});

    let addressObject = {
      lat: latitude,
      lng: longitude,
      location: formatted_address?.trim(),
      zipCode: [],
      zipIdValues: [],
      zipId: [],
    };
    const locality = firstAddressComponent.filter(function (ca) {
      if (isCalifornia) {
        return (
          ca.types?.[0] === "locality" ||
          ca.types?.[0] === "administrative_area_level_2"
        );
      } else {
        return ca.types?.[0] === "locality";
      }
    });
    const zip = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "postal_code";
    });
    if (zip.length) {
      addressObject.zip = _.get(zip?.[0], "short_name", "");
    }
    if (locality.length) {
      addressObject.city = _.get(locality?.[0], "long_name", "");
    } else {
      await getLocalityFromZipCode(addressObject);
    }

    const county = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_2";
    });

    if (county.length) {
      addressObject.county = _.get(county?.[0], "long_name", "");
    }

    const state = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_1";
    });
    if (state.length) {
      addressObject.state = _.get(state?.[0], "short_name", "");
    }
    const country = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "country";
    });
    //FORMATED ADDRESS WITH CITY,STATE and Country
    addressObject.location = `${addressObject.city}, ${addressObject.state}`;
    if (addressObject?.city == null || addressObject?.city == "") {
      setLocationError("Please enter a valid city and state. ");
      const isDisable = true;
      handleContinueButtonState(isDisable);
      clearAddressList();
    } else {
      setLocationError("");
      isGitchaAvailableLocation(addressObject);
      handleFormValues(addressObject);
      setZipCodeArray([]);
      setShowPolygon(false);
      clearAddressList();
    }
  };

  const clearAddressList = () => {
    setLocationList([]);
  };
  /**
   * To handle changes in map radius
   * @param {*} e
   */
  const handleMapRadiusChanges = (e) => {
    const value = _.get(e, "value", "");
    let zoomLvelBasedOnRadius;

    switch (value) {
      case 1:
        zoomLvelBasedOnRadius = 12;
        break;
      case 5:
        zoomLvelBasedOnRadius = 10;
        break;
      case 10:
        zoomLvelBasedOnRadius = 9;
        break;
      case 15:
        zoomLvelBasedOnRadius = 8;
        break;
      case 20:
        zoomLvelBasedOnRadius = 8;
        break;
      case 25:
        zoomLvelBasedOnRadius = 8;
        break;
      case 30:
        zoomLvelBasedOnRadius = 7;
        break;
      case 50:
        zoomLvelBasedOnRadius = 7;
        break;
    }
    handleFormValues({
      mapRadius: value,
      mapZoom: zoomLvelBasedOnRadius,
    });
  };
  /**
   * To handle neighbourhood value change
   * @param {*} e
   */
  const handleNeighborhoodChange = (e) => {
    const value = capitalizeString(handleTextBoxChange(e));
    handleFormValues({ neighborHood: value });
  };
  /**
   * To handle high school value change
   * @param {*} e
   */
  const handleHighSchoolChange = (e) => {
    const value = capitalizeString(handleTextBoxChange(e));
    handleFormValues({ highSchool: value });
  };
  /**
   * To handle changes in zipcodes field
   * @param {*} e
   */
  const handleZipCodeChange = (e) => {
    setLocationError("");
    setZipCodeEnterdValue(e.target.value);
    handleFormValues({
      location: "",
      city: "",
      state: "",
      zip: "",
      county: "",
      mapRadius: "",
      isGitchaAvailable: false,
      zipCode: zipCode,
    });
    const value = capitalizeString(e.target.value);
    const reqPayload = `address=${value}&components=country:US`;

    if (value.trim() !== "" && value?.length > 4) {
      getZipDetails({ reqPayload: reqPayload });
    } else {
      clearAddressList();
    }
  };
  /**
   * To get details of zipcodes entered
   * @param {*} param0
   */
  const getZipDetails = async ({ eventType, reqPayload }) => {
    clearZipCodeList();
    await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");

      if (results?.length > 0) {
        const formatted_address = _.get(results?.[0], "formatted_address", "");
        const firstLocationResult = _.get(results[0], "geometry.location", "");
        const firstAddressComponent = _.get(
          results[0],
          "address_components",
          "",
        );

        const lat = _.get(firstLocationResult, "lat", "");
        const lng = _.get(firstLocationResult, "lng", "");
        let addressObject = { lat: lat, lng: lng };

        try {
          if (firstAddressComponent && firstAddressComponent.length > 0) {
            const street_no = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "street_number";
            });

            if (street_no.length) {
              addressObject.street_no = _.get(street_no?.[0], "short_name", "");
            }

            const route = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "route";
            });

            if (route.length) {
              addressObject.route = _.get(route?.[0], "short_name", "");
              addressObject.street = _.get(route?.[0], "short_name", "");
            }
            const locality = firstAddressComponent.filter(function (ca) {
              if (isCalifornia) {
                return (
                  ca.types?.[0] === "locality" ||
                  ca.types?.[0] === "administrative_area_level_2"
                );
              } else {
                return ca.types?.[0] === "locality";
              }
            });
            if (locality.length) {
              addressObject.city = _.get(locality?.[0], "long_name", "");
            }
            const county = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "administrative_area_level_2";
            });
            if (county.length) {
              addressObject.county = _.get(county?.[0], "short_name", "");
            }

            const state = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "administrative_area_level_1";
            });
            if (state.length) {
              addressObject.state = _.get(state?.[0], "short_name", "");
            }
            const zip = firstAddressComponent.filter(function (ca) {
              return ca.types?.[0] === "postal_code";
            });
            if (zip.length) {
              addressObject.zip = _.get(zip?.[0], "short_name", "");
              results[0].zipCode = _.get(zip?.[0], "short_name", "");
              setZipList(results);
              handleFormValues({ forGitchaAvailablePopup: addressObject });
              isGitchaAvailableLocation(addressObject);
            }
          }
        } catch {}
      } else {
        const isDisable = true;
      }
    });
  };
  /**
   * To handle selection from location suggestions
   * @param {*} address
   */
  const handleZipCodeItemSelection = async (address) => {
    const exist = zipCodeArray.find((loc) => loc?.zipCode == address?.zipCode);

    if (!exist) {
      clearZipCodeList();
      setZipCodeArray([
        ...zipCodeArray,
        { zipCode: address?.zipCode, zipId: null },
      ]);
      setZipCodeEnterdValue("");
      const formatted_address = _.get(address, "formatted_address", "");
      const firstAddressComponent = _.get(address, "address_components", "");
      const latitude = _.get(address, "geometry.location.lat", ""); // removeFixedDecimalPart({value: _.get(address, "geometry.location.lat", ""), decimalPoint : DECIMAL_POINTS_IN_COORDICATES});
      const longitude = _.get(address, "geometry.location.lng", ""); // removeFixedDecimalPart({value: _.get(address, "geometry.location.lng", ""), decimalPoint : DECIMAL_POINTS_IN_COORDICATES});

      let addressObject = {
        lat: "",
        lng: "",
        location: "",
        mapRadius: null,
        zipCode: [...zipCodeArray, { zipCode: address?.zipCode, zipId: null }],
      };

      handleFormValues(addressObject);
      clearZipCodeList();
      setShowPolygon(true);
      setShowMarker(false);
    } else {
      setZipCodeEnterdValue("");
      clearZipCodeList();
    }
  };

  /**
   * To handle remove location from selected zipcodes list
   * @param {*} removeLocation
   */
  const handleRemoveLocation = (removeLocation) => {
    const someArray = zipIdValues.filter(
      (loc) => loc?.zipCode != removeLocation?.zipCode,
    );
    handleFormValues({
      zipCode: someArray,
      zipIdValues: someArray,
      focusOut: true,
    });
    setZipCodeArray(someArray);
  };

  const clearZipCodeList = () => {
    setZipList([]);
  };
  const handleCombinedInputChange = (e) => {
    const value = e.target.value;

    // Check if input is a ZIP code (numeric and length 5)
    if (/^\d+$/.test(value) && value.length <= 5) {
      handleZipCodeChange(e);
    } else {
      handleLocationChange(e);
    }
  };

  return (
    <>
      {!location && !lat && !lng && (
        <GeoLocation handleFormValues={handleFormValues} />
      )}
      <div className={!isEdit ? "center-content-sec mw-100 mb-12" : ""}>
        <div className="wantad-location-wrap mb-0">
          <div className="row">
            <div className="col-md-6">
              <div className="common-input-wrap mb-0">
                {!isEdit && (
                  <Form.Label>
                    Location<span>*</span>
                  </Form.Label>
                )}
                <div className="position-relative mb-10">
                  {locationList.length > 0 ? (
                    <Button
                      variant=""
                      className="search-clear-btn"
                      onClick={() => clearAddressList()}
                    ></Button>
                  ) : (
                    ""
                  )}
                  <InputGroupControl
                    disabled={!lat && !lng ? true : false}
                    inputGroupClass="wantad-location-search position-relative mb-0"
                    ariaLabel="Example"
                    ariaDescribedBy="basic-addon3"
                    placeholder="Enter City and state or ZIP code"
                    errorMessage={locationError}
                    value={zipCodeEnterdValue || location || ""}
                    iconClass="search-icon"
                    isIconEnable={true}
                    iconId="button-addon3"
                    onChange={(e) => {
                      handleCombinedInputChange(e);
                    }}
                    onBlur={() => {
                      handleFormValues({ focusOut: true });
                    }}
                  />
                  {locationList.length > 0 && (
                    <div className="location-result-wrap">
                      <ul>
                        {locationList?.map((address, i) => {
                          return (
                            <li key={i}>
                              <a
                                role="button"
                                href={void 0}
                                onClick={(e) => {
                                  handleItemSelection(address);
                                }}
                              >
                                {_.get(address, "formatted_address", "")}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Show ZIP suggestions */}
                  {zipList.length > 0 && (
                    <div className="location-result-wrap">
                      <ul>
                        {zipList.map((address, i) => (
                          <li key={i}>
                            <a
                              onClick={() =>
                                handleZipCodeItemSelection(address)
                              }
                            >
                              {address.zipCode}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Show selected ZIP tags */}
                  {zipCodeArray.length > 0 && (
                    <div className="zipcode-tag-wrap">
                      {zipCodeArray.map((loc, i) => (
                        <div key={i} className="zipcode-tag me-3">
                          <button
                            className="btn zipcode-close-btn me-1"
                            onClick={() => handleRemoveLocation(loc)}
                          >
                            <img src={ZipCloseIcon} alt="remove" />
                          </button>
                          {loc?.zipCode}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="common-input-wrap mb-0">
                <div className="lot-select-wrap mb-10">
                  <Form.Label>
                    Radius<span>*</span>
                  </Form.Label>
                  <SelectDropDown
                    name="colors3"
                    options={radiusList || []}
                    className="lotsize-select"
                    classNamePrefix="select"
                    placeholder="Select mile radius"
                    value={filterArrayBasedOnKey({
                      list: radiusList,
                      filterKey: "value",
                      value: mapRadius,
                    })}
                    onChange={(e) => handleMapRadiusChanges(e)}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                    }}
                    styles={{
                      dropdownIndicator: (provided, state) => ({
                        ...provided,
                        transform:
                          state.selectProps.menuIsOpen && "rotate(180deg)",
                      }),
                    }}
                    errorClass="error-msg error-msg-height"
                    disabled={zipCodeArray && zipCodeArray.length > 0}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="common-input-wrap mb-2">
                <Form.Label>Preferred Neighborhood</Form.Label>
                <TextBox
                  type="text"
                  value={neighborHood}
                  onChange={(e) => {
                    handleNeighborhoodChange(e);
                  }}
                  placeholder="Neighborhood"
                ></TextBox>
              </div>
            </div>
            {adType !== SHORT_RENT && (
              <div className="col-md-6">
                <div className="common-input-wrap mb-2">
                  <Form.Label>Preferred High School</Form.Label>
                  <TextBox
                    type="text"
                    value={highSchool}
                    onChange={(e) => {
                      handleHighSchoolChange(e);
                    }}
                    placeholder="High School"
                  ></TextBox>
                </div>
              </div>
            )}
            {isCalifornia ? (
              <div className="col-md-12">
                <div className="common-input-wrap mb-2">
                  {adType === BUY ? (
                    <Form.Label>
                      Have you been impacted by the recent LA fires?.
                    </Form.Label>
                  ) : (
                    <Form.Label>
                      Have you been impacted by recent natural disasters? We’re
                      here to help.
                    </Form.Label>
                  )}
                  <FireDisplacedCheckbox
                    isChecked={fireDisplaced}
                    handleFormValues={handleFormValues}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className="googlemap-responsive">
            <div className="map-outer" ref={targetRef}>
              <WantAdGoogleMap
                defaultProps={defaultProps}
                mapHeight="100%"
                mapWidth="100%"
                handleFormValues={handleFormValues}
                mapType={mapType}
                mapRadius={mapRadius}
                handleShowMarker={handleShowMarker}
                HandleMapClickOrDrag={HandleMapClickOrDrag}
                isClickOrDrag={isClickOrDrag}
                showMarker={showMarker}
                getAddresseDetails={getAddresseDetails}
                showPolygon={showPolygon}
                zipCode={zipCode}
                zipIdValues={zipIdValues}
                handleItemSelection={handleItemSelection}
              />
            </div>
          </div>
          {!zipCode?.length > 0 && (
            <div className="google-map-label mb-24">
              Drag location pin to your desired area
            </div>
          )}
        </div>
        <GitchaNotAvailableModal
          show={gitchaModalShow}
          onHide={() => handleGitchaAvailableModal(false)}
        />
      </div>
    </>
  );
};
export default WantAdLocation;
